import React, { useState, useEffect } from 'react';
import { X, BarChart3, Clock, Calendar, LogIn, LogOut } from 'lucide-react';
import { Button as UIButton } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { formatTimeDuration } from '../utils/timeFormat';
import axios from 'axios';


const API = "/api";

const TimeTrackingModal = ({ product, isOpen, onClose }) => {
  const [timeData, setTimeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && product) {
      fetchTimeTrackingData();
    }
  }, [isOpen, product]);

  const fetchTimeTrackingData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products/${product.id}/time-tracking`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
        }
      });
      setTimeData(response.data);
    } catch (error) {
      console.error('Failed to fetch time tracking data:', error);
      toast({
        title: "Error",
        description: "Failed to load time tracking data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour12: false
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDuration = (hours) => {
    return formatTimeDuration(hours);
  };

  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">
                Time Tracking - {product.title}
              </h2>
              <p className="text-sm text-slate-600">
                View detailed time tracking information
              </p>
            </div>
          </div>
          <UIButton
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700"
          >
            <X className="w-5 h-5" />
          </UIButton>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-slate-600 mt-2">Loading time tracking data...</p>
            </div>
          ) : timeData ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Hours</CardTitle>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatDuration(timeData.total_hours)}</div>
                    <p className="text-xs text-muted-foreground">
                      Across all sessions
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Days</CardTitle>
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{timeData.total_days}</div>
                    <p className="text-xs text-muted-foreground">
                      Days with activity
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Current Status</CardTitle>
                    {timeData.currently_logged_in ? (
                      <LogIn className="h-4 w-4 text-green-600" />
                    ) : (
                      <LogOut className="h-4 w-4 text-slate-400" />
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {timeData.currently_logged_in ? (
                        <Badge className="bg-green-100 text-green-800">Logged In</Badge>
                      ) : (
                        <Badge variant="outline">Not Logged In</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {timeData.currently_logged_in ? 'Active session' : 'No active session'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Daily Hours Breakdown */}
              {Object.keys(timeData.daily_hours).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Daily Hours Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(timeData.daily_hours)
                        .sort(([a], [b]) => new Date(b) - new Date(a))
                        .slice(0, 10) // Show last 10 days
                        .map(([date, hours]) => (
                          <div key={date} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">
                                {formatDate(date)}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {formatDuration(hours)}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-32 bg-slate-200 rounded-full h-2">
                                <div
                                  className="bg-blue-600 h-2 rounded-full"
                                  style={{
                                    width: `${Math.min((hours / 8) * 100, 100)}%` // 8h = 100%
                                  }}
                                ></div>
                              </div>
                              <span className="text-sm text-slate-600">
                                {formatDuration(hours)}
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Session History */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Session History</CardTitle>
                </CardHeader>
                <CardContent>
                  {timeData.sessions.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No session history found</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {timeData.sessions.map((session) => (
                        <div
                          key={session.id}
                          className="flex items-center justify-between p-3 border border-slate-200 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              {session.is_active ? (
                                <LogIn className="w-4 h-4 text-green-600" />
                              ) : (
                                <LogOut className="w-4 h-4 text-slate-400" />
                              )}
                              <span className="text-sm font-medium">
                                {formatDateTime(session.login_time)}
                              </span>
                            </div>
                            {session.logout_time && (
                              <span className="text-sm text-slate-500">
                                → {formatDateTime(session.logout_time)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={session.is_active ? "default" : "outline"}>
                              {session.is_active ? "Active" : "Completed"}
                            </Badge>
                            <span className="text-sm font-medium text-slate-700">
                              {formatDuration(session.duration_hours)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No time tracking data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeTrackingModal;